import {
  ALL_FILTER_VALUE,
  defaultTemplatesFilters,
  organizationTemplatesFiltersAtom,
} from '@/app-shell/settings/organization/atoms/organizationAtoms';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { enumToDescription } from '@/utils/enumToDescription';
import { useTranslations } from '@heidi/runtime/i18n';
import {
  type Shared__enums__enums__SortOrder as SortOrder,
  type TemplateSortBy,
  TemplateType,
} from '@heidi/schema/ml-scribe';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useOrganizationTemplatesQuery } from './useOrganizationTemplatesQuery';

export interface FilterOption {
  id: string;
  value: string;
  label: string;
}

export enum TemplateFilterKey {
  TYPE = 'types',
  CREATOR = 'creatorIds',
  VISIBILITY = 'visibilities',
}

export interface FilterConfig {
  key: TemplateFilterKey;
  icon: string; // We'll map this to actual icons in the component
  label: string;
  options: FilterOption[];
  selectedValue: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  convertValueToLabel: (value: string) => string;
}

export const TEMPLATE_ONLY_NOTE_DOCUMENT = Object.values(TemplateType).filter(
  (category) => category === TemplateType.DOCUMENT || category === TemplateType.NOTE
);

export const useFilters = () => {
  const [filters, setFilters] = useAtom(organizationTemplatesFiltersAtom);
  const { orgTeams } = useCombinedOrganizationInfo();
  const { templatesMetadata } = useOrganizationTemplatesQuery(filters);
  const [creators, setCreators] = useState<Record<string, string>>({});
  useEffect(() => {
    setCreators(templatesMetadata?.creators ?? {});
  }, [templatesMetadata]);
  const { t } = useTranslations();

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        key: TemplateFilterKey.TYPE,
        icon: 'File',
        label: 'Type',
        options: Object.values(TEMPLATE_ONLY_NOTE_DOCUMENT).map((type) => ({
          id: type,
          value: type,
          label:
            enumToDescription[type] === 'Note'
              ? t('organization.templatesLibrary.note')
              : t('organization.templatesLibrary.document'),
        })),
        selectedValue: ALL_FILTER_VALUE,
        searchPlaceholder: t('organization.templatesLibrary.searchTemplates'),
        emptyMessage: t('organization.templatesLibrary.noTemplatesFound'),
        convertValueToLabel: (value: string) => {
          if (value === ALL_FILTER_VALUE) {
            return t('organization.all');
          } else if (value === String(TemplateType.DOCUMENT)) {
            return t('organization.templatesLibrary.document');
          } else if (value === String(TemplateType.NOTE)) {
            return t('organization.templatesLibrary.note');
          }
          return value;
        },
      },
      {
        key: TemplateFilterKey.CREATOR,
        icon: 'User',
        label: t('organization.templatesLibrary.creator'),
        options: creators
          ? Object.entries(creators).map(([id, name]) => ({
              id,
              value: id,
              label: String(name),
            }))
          : [],
        selectedValue: ALL_FILTER_VALUE,
        convertValueToLabel: (value: string) => {
          return creators[value] || '';
        },
      },
      {
        key: TemplateFilterKey.VISIBILITY,
        icon: 'Eye',
        label: t('organization.templatesLibrary.visibility'),
        options:
          orgTeams?.map((team) => ({
            id: team.id,
            value: team.id,
            label: team.name,
          })) || [],
        selectedValue: ALL_FILTER_VALUE,
        searchPlaceholder: t('organization.templatesLibrary.searchForTeam'),
        emptyMessage: t('organization.templatesLibrary.noResultsFound'),
        convertValueToLabel: (value: string) =>
          orgTeams?.find((team) => team.id === value)?.name || '',
      },
    ],
    [orgTeams, t, creators]
  );

  // Handle sortby change
  const handleSortByChange = useCallback(
    (value: TemplateSortBy, order: SortOrder) => {
      setFilters((prev) => ({
        ...prev,
        sortBy: value,
        order: order,
        page: 1,
      }));
    },
    [setFilters]
  );

  // Handle filter changes
  const handleFilterChange = useCallback(
    (filterKey: string, value: string | string[]) => {
      setFilters((prev) => ({
        ...prev,
        [filterKey]: value,
        page: 1,
      }));
    },
    [setFilters]
  );
  const handlePageChange = useCallback(
    (page: number) => {
      setFilters((prev) => ({
        ...prev,
        page,
      }));
    },
    [setFilters]
  );
  // Handle search change debounced
  const handleSearchChangeDebounced = useDebouncedCallback((value: string) => {
    handleFilterChange('keyword', value);
  }, 300);

  // Handle reset filters
  const handleResetFilters = useCallback(() => {
    setFilters(defaultTemplatesFilters);
  }, [setFilters]);

  // Check if any filters are active
  const showResetFilters = useMemo(() => {
    return (
      filters.keyword ||
      filters.types !== ALL_FILTER_VALUE ||
      filters.creatorIds !== ALL_FILTER_VALUE ||
      filters.visibilities !== ALL_FILTER_VALUE
    );
  }, [filters]);

  // Build query input for API calls
  const queryInput = useMemo(
    () => ({
      page: filters.page,
      pageSize: filters.pageSize,
      sortBy: filters.sortBy,
      order: filters.order,
      search: filters.keyword,
      typeIds: filters.types !== ALL_FILTER_VALUE ? [filters.types] : [],
      creatorIds: filters.creatorIds !== ALL_FILTER_VALUE ? [filters.creatorIds] : [],
      visibilityIds: filters.visibilities.length > 0 ? filters.visibilities : [],
    }),
    [filters]
  );

  return {
    filterConfigs,
    filters,
    showResetFilters,
    queryInput,
    handleFilterChange,
    handleSortByChange,
    handleSearchChangeDebounced,
    handleResetFilters,
    setFilters,
    handlePageChange,
  };
};
